import { useState } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Edit, Mail, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import type { SequenceWithRelations } from "@shared/schema";
import { EditableStatus } from "@/components/editable-status";
import { TaggableDateCell } from "@/components/taggable-date-cell";
import { EditSequenceDialog } from "@/components/edit-sequence-dialog";
import { AddTaskDialog } from "@/components/add-task-dialog";
import { useSequenceConfig } from "@/contexts/sequence-config-context";
import { apiRequest } from "@/lib/queryClient";
import { TREE_COLUMNS, TREE_GRID_TEMPLATE, TREE_GRID_TEMPLATE_WITH_BUDGET, getTreeColumnStyles } from "@/lib/tree-columns";
import { getUserColor } from '@/lib/user-color';
import { TimeTrackButton } from "@/components/timetrack";
import { TimeTrackSummary } from "@/components/timetrack-summary";

interface SequenceRowProps {
  sequence: SequenceWithRelations;
  projectId: string;
  projectStatus?: string;
  isSelected?: boolean;
  onToggleSelection?: (sequenceNumber: string) => void;
  showBudgetColumn?: boolean;
}

export function SequenceRow({
  sequence,
  projectId,
  projectStatus,
  isSelected = false,
  onToggleSelection,
  showBudgetColumn = false
}: SequenceRowProps) {
  const queryClient = useQueryClient();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [addPulseDialogOpen, setAddPulseDialogOpen] = useState(false);
  
  const { data: artists = [] } = useQuery<any[]>({ queryKey: ['/api/artists'] });
  
  // Get color instantly from global context - no API calls needed
  const { sequenceColors } = useSequenceConfig();
  const sequenceColor = sequence.color || sequenceColors[sequence.sequenceNumber] || '#3B82F6';

  const updateSequenceMutation = useMutation({
    mutationFn: async (updates: Partial<SequenceWithRelations>) => {
      const response = await apiRequest('PATCH', `/api/sequences/${sequence.id}`, updates);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/years'] });
      queryClient.invalidateQueries({ queryKey: ['/api/projects'] });
    },
  });

  const handleStatusChange = (newStatus: string) => {
    updateSequenceMutation.mutate({ status: newStatus });
  };

  const handleTaskStatusChange = (newTaskStatus: string) => {
    updateSequenceMutation.mutate({ taskStatus: newTaskStatus });
  };

  // Colors are now loaded instantly from global context - no async loading needed

  return (
    <div id={`sequence-row-${sequence.id}`} className="tree-item border-b border-gray-600 relative">
      <div className="grid px-3 items-center gap-0" style={{ gridTemplateColumns: showBudgetColumn ? TREE_GRID_TEMPLATE_WITH_BUDGET : TREE_GRID_TEMPLATE, ...getTreeColumnStyles(showBudgetColumn) }}>
        {/* Thumbnail Placeholder Column - Maintains alignment with project rows */}
        <div className="flex items-center justify-center border-r border-gray-600" />
        
        {/* Empty Expand/Collapse Column - sequences don't have children */}
        <div className="flex items-center justify-center border-r border-gray-600">
          {/* No expand button for sequences */}
        </div>
        
        {/* Sequence Number Column */}
        <div className="flex items-center border-r border-gray-600 px-2">
          <button
            className={cn(
              "text-xs font-medium px-1.5 py-0.5 rounded text-white border-0 whitespace-nowrap transition-all cursor-pointer hover:opacity-80",
              isSelected && "ring-2 ring-white ring-opacity-70"
            )}
            style={{ backgroundColor: sequenceColor }}
            onClick={() => onToggleSelection?.(sequence.sequenceNumber)}
            title={isSelected ? "Click to hide pulses from this sequence" : "Click to show only pulses from this sequence"}
          >
            {sequence.sequenceNumber}
          </button>
        </div>

        {/* Sequence Name Column */}
        <div className="min-w-0 px-3 border-r border-gray-600">
          <span className="text-sm truncate block">
            {sequence.name || "Unnamed sequence"}
          </span>
        </div>

        {/* Lead Artist Column */}
        <div className="flex items-center px-2">
          {sequence.leadArtistUser && (
            <div className="relative inline-flex">
              <div 
                className="px-1.5 py-0.5 rounded flex items-center justify-center text-xs font-medium text-white min-w-[22px]"
                style={{ backgroundColor: getUserColor(sequence.leadArtistUser.colorCode) }}
              >
                <span className="text-xs">{sequence.leadArtistUser.shortCode}</span>
              </div>
            </div>
          )}
        </div>

        {/* Team Column */}
        <div className="flex items-center pl-2">
          <div className="flex flex-col items-start space-y-0.5">
            {sequence.artists && sequence.artists.slice(0, 3).map((artist: any, index: number) => {
              const artistData = artists.find((a: any) => a.shortCode === artist.artistCode);
              return (
                <div key={artist.artistCode} className="relative inline-flex">
                  <div 
                    className="px-1.5 py-0.5 rounded flex items-center justify-center border border-gray-600 text-xs font-medium text-white min-w-[22px] z-10"
                    style={{ 
                      backgroundColor: getUserColor(artistData?.colorCode),
                      zIndex: 10 - index
                    }}
                    title={`${artist.artistCode}`}
                  >
                    <span className="text-xs">{artist.artistCode}</span>
                  </div>
                </div>
              );
            })}
            {sequence.artists && sequence.artists.length > 3 && (
              <span className="text-xs text-gray-400 ml-1">
                +{sequence.artists.length - 3}
              </span>
            )}
          </div>
        </div>

        {/* Project Status Column */}
        <div className="px-2">
          <EditableStatus 
            status={sequence.status || "Not Started"} 
            type="project"
            onStatusChange={handleStatusChange}
          />
        </div>

        {/* Status Column */}
        <div className="px-2">
          <EditableStatus 
            status={sequence.taskStatus || "working_on_it"}
            type="task"
            onStatusChange={handleTaskStatusChange}
          />
        </div>

        {/* Timing Column */}
        <div className="px-2">
          <TaggableDateCell 
            entityType="sequence"
            entityId={sequence.id}
            taggedDates={sequence.taggedDates || []}
            // Pass the SEQUENCE's own status, not the parent project's, so a
            // finalized parent doesn't turn an in-progress sequence's deadline
            // green and an in-progress parent doesn't suppress green on a
            // sequence whose own status is final. The cell's color helper
            // treats projectStatus === "final" || taskStatus === "done" as the
            // "show deadline / due-date in green" trigger; feeding the
            // sequence's fields makes that evaluation per-sequence.
            projectStatus={sequence.status}
            taskStatus={sequence.taskStatus}
          />
        </div>

        {/* Actions Column */}
        <div className="flex items-center justify-end space-x-1">
          <TimeTrackSummary projectId={projectId} targetType="sequence" targetId={sequence.id} />
          <TimeTrackButton projectId={projectId} targetType="sequence" targetId={sequence.id} />
          <Button
            variant="ghost"
            size="sm"
            className="text-gray-400 hover:text-white p-1 h-auto"
            title="Edit"
            onClick={() => setEditDialogOpen(true)}
          >
            <Edit className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-gray-400 hover:text-white p-1 h-auto"
            title="Messages"
          >
            <Mail className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-gray-400 hover:text-white p-1 h-auto"
            title="Add Pulse"
            onClick={() => setAddPulseDialogOpen(true)}
            data-testid={`button-add-pulse-seq-${sequence.sequenceNumber}`}
          >
            <Plus className="w-4 h-4" />
          </Button>
        </div>
        {showBudgetColumn && <div />}
      </div>
      
      {/* Edit Sequence Dialog */}
      <EditSequenceDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        sequence={sequence}
        projectId={projectId}
      />
      
      {/* Add Pulse Dialog with sequence pre-assigned */}
      <AddTaskDialog
        projectId={projectId}
        open={addPulseDialogOpen}
        onOpenChange={setAddPulseDialogOpen}
        initialSequences={[sequence.sequenceNumber]}
      />
    </div>
  );
}